import { useRef, useState } from "react";
import { adminUploadFile } from "@/lib/api";
import { Music, Loader2, X, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AudioUploader({ value, onChange, label = "Audio", testid = "audio" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("audio/")) { toast.error("Please choose an audio file (MP3, WAV, OGG or M4A)."); return; }
    if (file.size > 40 * 1024 * 1024) { toast.error("Audio too large (max 40 MB)."); return; }
    setBusy(true);
    try {
      const res = await adminUploadFile(file);
      const url = res.url?.startsWith("http") ? res.url : `${BACKEND_URL}${res.url}`;
      onChange(url);
      toast("Audio uploaded");
    } catch (err) {
      toast.error("Upload failed", { description: err.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">{label}</label>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            data-testid={`${testid}-upload-btn`}
            className="inline-flex items-center gap-2 border border-line px-3 py-2 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Music className="h-4 w-4" />}
            {busy ? "Uploading…" : "Upload audio"}
          </button>
          {value && (
            <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1 text-xs text-ink-3 hover:text-[var(--saffron-dark)]">
              <X className="h-3.5 w-3.5" /> Remove
            </button>
          )}
        </div>
        {value && <audio src={value} controls className="w-full" data-testid={`${testid}-preview`} />}
        <div className="flex items-center gap-2">
          <LinkIcon className="h-3.5 w-3.5 shrink-0 text-ink-3" />
          <input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="or paste an audio URL"
            data-testid={`${testid}-url-input`}
            className="w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none"
          />
        </div>
        <input ref={inputRef} type="file" accept="audio/*" onChange={onFile} className="hidden" data-testid={`${testid}-file-input`} />
      </div>
    </div>
  );
}
